/*
 * Settings page, keymap editor, key recorder and the custom sections (W2-13 settings-keymap).
 * Design tokens only (src/ui/styles/tokens.css); state is never shown by colour alone (icons and words go with it).
 * Layouts hold at 1280x600: the navigation narrows, rows stack their control under the text below 900 px.
 */

/* ---------------------------------------------------------------- page */

.page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--color-bg);
  color: var(--color-text);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding: var(--space-3) var(--space-5);
  border-bottom: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
}

.headerTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text);
}

.h1 {
  font-size: var(--text-xl);
}

.pageSearch {
  flex: 1 1 280px;
  max-width: 520px;
}

.headerActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

/* ---------------------------------------------------------------- navigation */

.nav {
  flex: 0 0 auto;
  width: calc(208px * var(--ui-scale));
  padding: var(--space-4) var(--space-3);
  border-right: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
  overflow-y: auto;
}

.navList {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  margin: 0;
  padding: 0;
  list-style: none;
}

.navLink {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: var(--text-md);
}

.navLink:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.navLink[aria-current='location'] {
  background: var(--color-accent-muted);
  color: var(--color-text);
  box-shadow: inset 3px 0 0 var(--color-accent);
  font-weight: var(--weight-medium);
}

.navLink[data-empty='true'] {
  color: var(--color-text-disabled);
}

.navTitle {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navCount {
  min-width: 1.6em;
  padding: 0 var(--space-1);
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  text-align: center;
}

/* ---------------------------------------------------------------- content */

.content {
  flex: 1 1 auto;
  min-width: 0;
  padding: var(--space-5) var(--space-6) var(--space-8);
  overflow-y: auto;
  scroll-padding-top: var(--space-4);
}

.searchStatus {
  margin-bottom: var(--space-4);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.section {
  max-width: 1080px;
  margin: 0 auto var(--space-7);
  scroll-margin-top: var(--space-4);
}

.sectionHeader {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding-bottom: var(--space-3);
  margin-bottom: var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  color: var(--color-text-muted);
}

.sectionTitle {
  color: var(--color-text);
  font-size: var(--text-lg);
}

.sectionDesc {
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
}

.items {
  display: flex;
  flex-direction: column;
}

.groupTitle {
  margin: var(--space-4) 0 var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  text-transform: none;
}

/* ---------------------------------------------------------------- rows */

.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 380px) auto;
  align-items: center;
  gap: var(--space-2) var(--space-5);
  padding: var(--space-3) var(--space-3);
  border-radius: var(--radius-md);
  scroll-margin: var(--space-6);
  transition: background-color var(--duration-slow) var(--ease-standard);
}

.row + .row {
  border-top: var(--border-width) solid var(--color-divider);
}

.row:hover {
  background: var(--color-surface-1);
}

.row[data-highlighted='true'] {
  background: var(--color-accent-muted);
  box-shadow: inset 0 0 0 var(--border-width) var(--color-accent-border);
}

.rowText {
  min-width: 0;
}

.rowTitleLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.rowTitle {
  color: var(--color-text);
  font-weight: var(--weight-medium);
}

.rowDesc {
  margin-top: var(--space-0);
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
}

.rowControl {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  min-width: 0;
}

.rowControl > * {
  min-width: 0;
}

.rowReset {
  display: flex;
  justify-content: flex-end;
}

.changed {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-accent-border);
  border-radius: var(--radius-pill);
  color: var(--color-accent);
  font-size: var(--text-2xs);
  font-weight: var(--weight-medium);
}

.number {
  width: calc(150px * var(--ui-scale));
  max-width: 100%;
}

.slider {
  width: calc(220px * var(--ui-scale));
  max-width: 100%;
}

.nullable {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.choice {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
  min-width: 0;
}

.choiceDesc {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  text-align: right;
}

.inline {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.status {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

@media (max-width: 900px) {
  .nav {
    width: calc(64px * var(--ui-scale));
    padding: var(--space-3) var(--space-1);
  }

  .navTitle,
  .navCount {
    display: none;
  }

  .navLink {
    justify-content: center;
  }

  .content {
    padding: var(--space-4);
  }

  .row {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .rowControl {
    grid-column: 1 / 2;
    justify-content: flex-start;
  }

  .choice {
    align-items: flex-start;
  }

  .choiceDesc {
    text-align: left;
  }
}

/* ---------------------------------------------------------------- search */

.search {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--control-h-lg);
  padding: 0 var(--space-1) 0 var(--space-3);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text-subtle);
}

.search:focus-within {
  border-color: var(--color-accent);
}

/* key capture ("find by keys"): the box listens for keys instead of text */
.search[data-capturing='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

.search[data-capturing='true'] .searchInput {
  caret-color: transparent;
}

.searchInput {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  color: var(--color-text);
}

.searchInput::placeholder {
  color: var(--color-text-subtle);
}

.searchInput::-webkit-search-cancel-button {
  display: none;
}

.searchInput:focus-visible {
  outline: none;
}

/* ---------------------------------------------------------------- blocks */

.block {
  padding: var(--space-3) 0;
  border-radius: var(--radius-md);
  scroll-margin: var(--space-6);
}

.block[data-highlighted='true'] {
  box-shadow: inset 0 0 0 var(--border-width) var(--color-accent-border);
}

.blockHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-3);
}

.blockTitle {
  color: var(--color-text);
  font-size: var(--text-md);
}

.blockDesc {
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
  max-width: 76ch;
}

.subTitle {
  margin: var(--space-5) 0 var(--space-1);
  color: var(--color-text);
  font-size: var(--text-md);
}

.facts {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: var(--space-3) 0;
  padding: 0;
  list-style: none;
}

.facts > li {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  color: var(--color-text-muted);
}

.facts > li > svg {
  flex: 0 0 auto;
}

.facts > li > span {
  flex: 1 1 0;
  min-width: 12em;
}

.facts > li[data-level='ok'] > svg {
  color: var(--color-success);
}

.facts > li[data-level='warning'] > svg {
  color: var(--color-warning);
}

.facts > li[data-level='error'] > svg {
  color: var(--color-danger);
}

.facts > li[data-level='info'] > svg {
  color: var(--color-info);
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-success-muted);
  color: var(--color-success);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
}

.simpleTable {
  width: 100%;
  margin: var(--space-3) 0;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.simpleTable th,
.simpleTable td {
  padding: var(--space-1) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  text-align: left;
  vertical-align: top;
}

.simpleTable th {
  color: var(--color-text-subtle);
  font-weight: var(--weight-medium);
}

.tableCaption {
  padding: var(--space-2) 0;
  color: var(--color-text);
  font-weight: var(--weight-semibold);
  text-align: left;
}

.link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-link);
}

/* ---------------------------------------------------------------- skins */

.skins,
.storage,
.rules,
.about {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.skins > button,
.storage > button,
.rules > button,
.about > button {
  align-self: flex-start;
}

.skinList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.skinItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.skinItem[data-selected='true'] {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.skinLabel {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  cursor: pointer;
}

label.skinItem {
  cursor: pointer;
}

.skinText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.skinName {
  color: var(--color-text);
  font-weight: var(--weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.skinMeta {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.skinItem input[type='radio'] {
  accent-color: var(--color-accent);
}

.skinProblems {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  margin-top: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-warning-muted);
  color: var(--color-text);
  font-size: var(--text-xs);
}

.skinProblems > svg {
  flex: none;
  margin-top: 2px;
  color: var(--color-warning);
}

.skinProblemsTitle {
  margin: 0;
  font-weight: var(--weight-medium);
}

.skinProblemList {
  margin: var(--space-1) 0 0;
  padding-left: var(--space-4);
}

/* ---------------------------------------------------------------- storage */

.trashList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: var(--space-2) 0 var(--space-3);
  padding: 0;
  list-style: none;
}

.trashItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
}

.trashText {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

/* ---------------------------------------------------------------- checker rules */

.ruleGroup {
  margin-top: var(--space-3);
}

.ruleGroupHeader {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.ruleList {
  display: flex;
  flex-direction: column;
  margin: var(--space-2) 0 0;
  padding: 0;
  list-style: none;
}

.ruleItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.ruleItem > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.ruleItem[data-off='true'] {
  color: var(--color-text-subtle);
}

/* ---------------------------------------------------------------- about */

.aboutHero {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-accent);
}

/* ---------------------------------------------------------------- keymap editor */

.keymap {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.kmToolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  position: sticky;
  top: calc(-1 * var(--space-5));
  z-index: 1;
  padding: var(--space-2) 0;
  background: var(--color-bg);
}

.kmSearch {
  flex: 1 1 260px;
  max-width: 420px;
}

.kmCaptureHint {
  flex: 1 0 100%;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.kmActions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-left: auto;
}

.kmIssues,
.kmReport {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-warning);
  border-radius: var(--radius-md);
  background: var(--color-warning-muted);
}

.kmIssues > svg,
.kmReport[data-ok='false'] > svg {
  flex: 0 0 auto;
  color: var(--color-warning);
}

.kmReport[data-ok='true'] {
  border-color: var(--color-success);
  background: var(--color-success-muted);
}

.kmReport[data-ok='true'] > svg {
  color: var(--color-success);
}

.kmReportBody {
  flex: 1 1 auto;
  min-width: 0;
}

.kmIssuesTitle,
.kmReportTitle {
  color: var(--color-text);
  font-weight: var(--weight-medium);
}

.kmIssueList {
  margin: var(--space-1) 0 var(--space-2);
  padding-left: var(--space-5);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.kmLegend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.kmLegendItem {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.kmLegendItem[data-level='red'] > svg {
  color: var(--color-danger);
}

.kmLegendItem[data-level='yellow'] > svg {
  color: var(--color-info);
}

.kmCount {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.kmPageFilter {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.kmPageFilter > svg {
  flex: none;
}

.kmEmpty {
  padding: var(--space-6) 0;
  color: var(--color-text-muted);
  text-align: center;
}

.kmTable {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
  table-layout: fixed;
}

.kmTable thead th {
  padding: var(--space-1) var(--space-3);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  text-align: left;
}

.kmTable thead th:nth-child(1) {
  width: 34%;
}

.kmTable thead th:nth-child(2) {
  width: 24%;
}

.kmTable thead th:nth-child(4) {
  width: calc(72px * var(--ui-scale));
}

.kmGroup {
  content-visibility: auto;
  contain-intrinsic-size: auto 400px;
}

.kmCategory {
  padding: var(--space-4) var(--space-3) var(--space-1);
  color: var(--color-text);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  text-align: left;
  border-bottom: var(--border-width) solid var(--color-border);
}

.kmRow > td {
  padding: var(--space-2) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  vertical-align: top;
}

.kmRow:hover > td {
  background: var(--color-surface-1);
}

.kmRow[data-conflict='red'] > td:first-child {
  box-shadow: inset 3px 0 0 var(--color-danger);
}

.kmRow[data-conflict='yellow'] > td:first-child {
  box-shadow: none;
}

.kmCommand {
  display: table-cell;
}

.kmTitle {
  display: block;
  color: var(--color-text);
  font-weight: var(--weight-medium);
  overflow-wrap: anywhere;
}

.kmId {
  display: inline-block;
  margin-right: var(--space-2);
  color: var(--color-text-subtle);
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
}

.kmUnknown {
  color: var(--color-warning);
  font-size: var(--text-2xs);
}

.kmContext {
  color: var(--color-text-muted);
  overflow-wrap: anywhere;
}

.kmKeys {
  line-height: 2;
}

.kmNoKeys {
  color: var(--color-text-disabled);
  font-style: italic;
}

.kmRowActions {
  text-align: right;
  white-space: nowrap;
}

.kmConflictRow > td {
  padding: 0 var(--space-3) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.kmConflicts {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.kmConflicts > li {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.kmConflicts > li[data-level='red'] {
  background: var(--color-danger-muted);
  color: var(--color-text);
}

.kmConflicts > li[data-level='red'] > svg {
  color: var(--color-danger);
}

.kmConflicts > li[data-level='yellow'] {
  background: var(--color-info-muted);
}

.kmConflicts > li[data-level='yellow'] > svg {
  color: var(--color-info);
}

.conflictFix {
  color: var(--color-text-muted);
}

/* key chips */

.chip {
  display: inline-flex;
  align-items: center;
  margin: 0 var(--space-1) var(--space-1) 0;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  vertical-align: middle;
}

.chip[data-source='user'] {
  border-color: var(--color-accent-border);
}

.chip[data-conflict='red'] {
  border-color: var(--color-danger);
  background: var(--color-danger-muted);
}

/* A key shared with another situation is information, not a problem (UX-061): no warning colours. */
.chip[data-conflict='yellow'] {
  border-color: var(--color-border);
}

.chip[data-removed='true'] {
  border-style: dashed;
  background: transparent;
}

.chipMain,
.chipRemove {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
}

.chipMain:hover,
.chipRemove:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.chipRemove {
  padding: 0 var(--space-1);
  border-left: var(--border-width) solid var(--color-border);
}

.chip[data-conflict='red'] .chipMain > svg {
  color: var(--color-danger);
}

.chip[data-conflict='yellow'] .chipMain > svg {
  color: var(--color-info);
}

.chip[data-source='user'] .chipMain > svg:first-of-type {
  color: var(--color-accent);
}

.chipRemoved {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  opacity: 0.6;
  text-decoration: line-through;
}

/* a removal that belongs to the other preset (or to no preset key): dotted and quieter */
.chip[data-removed='elsewhere'] {
  border-style: dotted;
  background: transparent;
}

.chip[data-removed='elsewhere'] .chipRemoved {
  opacity: 0.45;
}

.chipTag {
  padding-right: var(--space-2);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  white-space: nowrap;
}

/* ---------------------------------------------------------------- recorder */

.recorder {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.recorderReplaces {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.capture {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(72px * var(--ui-scale));
  padding: var(--space-4);
  border: 2px dashed var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font-size: var(--text-lg);
}

.capture[data-listening='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

.capture kbd kbd {
  font-size: var(--text-md);
}

.captureListening {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-muted);
}

.recorderHint {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.recorderStatus {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-height: var(--control-h-md);
}

.verdict {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}

.verdict > svg {
  flex: 0 0 auto;
  margin-top: 2px;
}

.verdict[data-level='error'] {
  background: var(--color-danger-muted);
  color: var(--color-text);
}

.verdict[data-level='error'] > svg {
  color: var(--color-danger);
}

.verdict[data-level='warning'] {
  background: var(--color-warning-muted);
}

.verdict[data-level='warning'] > svg {
  color: var(--color-warning);
}

.verdict[data-level='info'] {
  background: var(--color-info-muted);
}

.verdict[data-level='info'] > svg {
  color: var(--color-info);
}

.verdict[data-level='ok'] {
  background: var(--color-success-muted);
}

.verdict[data-level='ok'] > svg {
  color: var(--color-success);
}

.recorderOptions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

@media (prefers-reduced-motion: reduce) {
  .row {
    transition: none;
  }
}
